import React from "react";
import { BrowserRouter } from "react-router-dom";
import { RecoilRoot } from "recoil";
import Router from "./Router";
import { Global, css } from "@emotion/react";
import * as Sentry from "@sentry/react";

import {
  AgPostalCodesStore,
  AuthStore,
  CsesStore,
  DelayedDeliveriesStore,
  LostDeliveriesStore,
  SentBackDeliveriesStore,
  DeliveryCommentsStore,
  DeliveriesStore,
  DeliveriesCollectedStore,
  DeliveriesNewStore,
  LoadingStore,
  ModalStore,
  PopupsStore,
  ProfileStore,
  SpotsStore,
} from "./stores";

import "bootstrap/dist/css/bootstrap.min.css";

const GlobalStyle = css`
  body {
    margin: 0;
  }

  *,
  :after,
  :before {
    box-sizing: border-box;
    font-size: 14px;
  }

  a,
  a:hover {
    color: inherit;
  }

  .modal {
    .modal-dialog {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      min-width: 100%;
      margin: 0;
      flex-direction: column;
    }

    .modal-content {
      width: 600px;
      border-radius: 0;
      flex: none;
      padding: 0 10px;
      postion: relative;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
    }

    .modal-lg {
      .modal-content {
        width: 800px;
      }
    }

    .modal-header {
      .close {
        padding: 13px;

        span {
          font-size: 26px;
        }
      }
    }

    .modal-body {
      max-height: calc(100vh - 200px);
      overflow: auto;
    }
  }

  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const SentryErrorBoundary = Sentry.ErrorBoundary;

const AppProvider = ({ contexts, children }) =>
  contexts.reduce(
    (prev, context) =>
      React.createElement(context, {
        children: prev,
      }),
    children,
  );

function App() {
  // 500번대 에러 상태 코드와 설명 매핑
  const SERVER_ERROR_TYPES = {
    500: {
      name: "internal_server_error",
      description: "Internal Server Error",
    },
    501: { name: "not_implemented", description: "Not Implemented" },
    502: { name: "bad_gateway", description: "Bad Gateway" },
    503: { name: "service_unavailable", description: "Service Unavailable" },
    504: { name: "gateway_timeout", description: "Gateway Timeout" },
    505: {
      name: "http_version_not_supported",
      description: "HTTP Version Not Supported",
    },
    506: {
      name: "variant_also_negotiates",
      description: "Variant Also Negotiates",
    },
    507: { name: "insufficient_storage", description: "Insufficient Storage" },
    508: { name: "loop_detected", description: "Loop Detected" },
    509: {
      name: "bandwidth_limit_exceeded",
      description: "Bandwidth Limit Exceeded",
    },
    510: { name: "not_extended", description: "Not Extended" },
    511: {
      name: "network_authentication_required",
      description: "Network Authentication Required",
    },
  };

  // 500번대 에러 체크 유틸리티 함수
  const is5xxError = (error) => {
    const status =
      error?.status || error?.statusCode || error?.response?.status;
    if (status && status >= 500 && status <= 599) {
      return status;
    }

    // 에러 메시지에서 상태 코드 찾기
    if (error?.message) {
      const match = error.message.match(/\b5\d{2}\b/);
      if (match) {
        return parseInt(match[0]);
      }
    }

    return null;
  };

  return (
    <>
      <Global styles={GlobalStyle} />
      <SentryErrorBoundary
        fallback={({ error }) => {
          const errorCode =
            error?.errorCode ??
            error?.code ??
            error?.status ??
            error?.statusCode ??
            "";
          const errorMessage = error?.errorMessage ?? error?.message ?? "";

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                textAlign: "center",
              }}
            >
              <div>{String(errorCode)}</div>
              <div>{String(errorMessage)}</div>
            </div>
          );
        }}
        showDialog={process.env.REACT_APP_ENV === "production"}
        beforeCapture={(scope, error) => {
          const statusCode = is5xxError(error);
          if (statusCode) {
            const errorType = SERVER_ERROR_TYPES[statusCode] || {
              name: `server_error_${statusCode}`,
            };

            scope.setTag("error_category", "5xx_error");
            scope.setTag("error_type", errorType.name);
            scope.setTag("http_status", statusCode.toString());
          }
        }}
      >
        <AppProvider
          contexts={[
            AgPostalCodesStore.Provider,
            AuthStore.Provider,
            CsesStore.Provider,
            DelayedDeliveriesStore.Provider,
            LostDeliveriesStore.Provider,
            SentBackDeliveriesStore.Provider,
            DeliveryCommentsStore.Provider,
            DeliveriesStore.Provider,
            DeliveriesCollectedStore.Provider,
            DeliveriesNewStore.Provider,
            LoadingStore.Provider,
            ModalStore.Provider,
            PopupsStore.Provider,
            ProfileStore.Provider,
            SpotsStore.Provider,
          ]}
        >
          <BrowserRouter>
            <RecoilRoot>
              <Router />
            </RecoilRoot>
          </BrowserRouter>
        </AppProvider>
      </SentryErrorBoundary>
    </>
  );
}

export default App;
